"use client";

import React, { Component, ReactNode } from 'react';
import { collectClientErrorEnv } from '@/libs/client-error-env';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  // When set, caught errors are POSTed to /api/error-report under this
  // context so widget crashes become visible in error_logs / alert emails.
  reportContext?: string;
  // When true, the error UI gets a "Try again" button that resets the
  // boundary and REMOUNTS the subtree (key bump). Safe recovery for the
  // externally-mutated-DOM commit crashes (insertBefore/removeChild): the
  // React tree above the boundary is intact, so a fresh mount heals the UI
  // without a full page reload. State that lives above the boundary survives.
  retry?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  resetKey: number;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, resetKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    if (!this.props.reportContext) return;
    const stack = `${error?.stack || ''}${errorInfo?.componentStack ? `\n${errorInfo.componentStack}` : ''}`;
    fetch('/api/error-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        context: this.props.reportContext,
        message: error?.message || 'Unknown client error',
        stack,
        // Translate/extension forensics (2026-09-14) — see libs/client-error-env.
        ...collectClientErrorEnv(),
      }),
    }).catch(() => {
      // Best-effort; never let reporting crash the boundary.
    });
  }

  handleReset = () => {
    this.setState((s) => ({ hasError: false, error: undefined, resetKey: s.resetKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      const retryBtn = this.props.retry ? (
        <button
          type="button"
          onClick={this.handleReset}
          className="btn btn-primary btn-sm"
          style={{ marginTop: 8 }}
        >
          Try again
        </button>
      ) : null;

      if (this.props.fallback !== undefined) {
        // Custom fallback (compact section UI): retry button rides below it.
        return (
          <>
            {this.props.fallback}
            {retryBtn}
          </>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-base-200">
          <div className="bg-base-100 p-8 rounded-lg shadow-lg max-w-md w-full">
            <h1 className="text-2xl font-bold text-error mb-4">Something went wrong</h1>
            <p className="text-base-content/70 mb-4">
              An unexpected error occurred. Please refresh the page to try again.
            </p>
            {this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-base-content/50">
                  Error details
                </summary>
                <pre className="mt-2 text-xs bg-base-200 p-2 rounded overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              {this.props.retry && (
                <button
                  type="button"
                  onClick={this.handleReset}
                  className="btn btn-outline flex-1"
                >
                  Try again
                </button>
              )}
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary flex-1"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Keyed fragment: handleReset's bump forces a full remount of the subtree.
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;
